/**
 * CaseloadToolbar — search / filter / sort / select toolbar above the
 * caseload list (issue #436, building on the desktop toolbar from
 * #433).
 *
 * Desktop (≥1024px): one inline row with search (left, flex-grow),
 * goal-area chips (scrollable middle), active-goals chips + sort
 * dropdown (right), plus the bulk-action bar slot below when one or
 * more rows are selected.
 *
 * Mobile (<1024px): search + a "Filters" pill (shows active-filter
 * count as a badge) inline; tapping the pill expands a slide-up
 * sheet containing the chip rows + sort dropdown. Esc closes the
 * sheet.
 *
 * The toolbar is a presentation-only shell. All state lives in
 * `CaseloadScreen`; this component fires callbacks on change. The
 * pure filter + sort + group helpers live in lib/caseload/filters
 * so the data flow is testable without rendering React.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { useBreakpoint } from '../lib/ui/useBreakpoint';
import type { SortKey } from '../lib/caseload/filters';
import {
  GOAL_AREAS,
  type GoalArea,
} from '../lib/effectiveslp/domain';

/**
 * Static label lookup — pulled out of the render so a regression
 * here surfaces in the file directly.
 */
const GOAL_AREA_LABEL: Record<GoalArea, string> = {
  articulation: 'Articulation',
  receptive_language: 'Receptive Language',
  expressive_language: 'Expressive Language',
  fluency: 'Fluency',
  social: 'Social',
  aac: 'AAC',
  voice: 'Voice',
  cognitive: 'Cognitive',
};

export { GOAL_AREA_LABEL };
export type { SortKey };

export type ActiveGoalsFilter = 'all' | 'with' | 'without';

export interface GoalAreaOption {
  value: GoalArea;
  label: string;
}

/**
 * The full set of goal-area chips the toolbar renders. We always
 * show the same 8 chips (plus "All"); the screen does not subset
 * this list to "categories present in the caseload" so a clinician
 * who filters to "Fluency" today still sees that chip when their
 * caseload happens to have zero fluency kids tomorrow.
 */
const GOAL_AREA_CHIPS: ReadonlyArray<GoalAreaOption> = [
  ...GOAL_AREAS.map((g) => ({ value: g, label: GOAL_AREA_LABEL[g] })),
];

export const GOAL_AREA_CHIPS_VALUE_ALL = 'all' as const;

export interface CaseloadToolbarProps {
  searchValue: string;
  onSearchChange: (next: string) => void;
  goalArea: string;
  onGoalAreaChange: (next: string) => void;
  needsNewSession: boolean;
  onNeedsNewSessionChange: (next: boolean) => void;
  activeGoals: ActiveGoalsFilter;
  onActiveGoalsChange: (next: ActiveGoalsFilter) => void;
  sort: SortKey;
  onSortChange: (next: SortKey) => void;
  /** Total filtered / total caseload, rendered below the search input. */
  totalStudents: number;
  filteredCount: number;
  /** Whether any non-search filter is active (used for the mobile badge). */
  hasActiveFilters: boolean;
  /** Optional placeholder override. */
  placeholder?: string;
  /** Toggle the desktop "select mode" (always-visible checkboxes). */
  selectMode: boolean;
  onToggleSelectMode: () => void;
  /** Body slot for the bulk-action bar (rendered inline on desktop). */
  bulkActionBar?: ReactElement | null;
}

const DEBOUNCE_MS = 200;

const SORT_LABELS: Record<SortKey, string> = {
  last_session: 'Last session',
  name: 'Name',
  created: 'Created',
  active_goals: 'Active goals',
};

const ACTIVE_GOALS_CHIPS: ReadonlyArray<{
  value: ActiveGoalsFilter;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'with', label: 'With active goals' },
  { value: 'without', label: 'Without active goals' },
];

const ACTIVE_GOAL_LABEL_FOR: Record<ActiveGoalsFilter, string> = {
  all: 'All',
  with: 'With active goals',
  without: 'Without active goals',
};

export function CaseloadToolbar({
  searchValue,
  onSearchChange,
  goalArea,
  onGoalAreaChange,
  needsNewSession,
  onNeedsNewSessionChange,
  activeGoals,
  onActiveGoalsChange,
  sort,
  onSortChange,
  totalStudents,
  filteredCount,
  hasActiveFilters,
  placeholder = 'Search by name…',
  selectMode,
  onToggleSelectMode,
  bulkActionBar,
}: CaseloadToolbarProps): ReactElement {
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop' || bp === 'wide';
  // Local input draft for instant feedback; the effect debounces
  // the value to the parent.
  const [draft, setDraft] = useState(searchValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Mobile filter sheet (open/closed).
  const [sheetOpen, setSheetOpen] = useState(false);

  // aria-controls must reference the element whose visibility is
  // toggled (the sheet container), NOT the dialog's heading.
  // aria-labelledby on the sheet still points at the heading so the
  // dialog's accessible name stays correct (#490).
  const headingId = useId();
  const sheetId = useId();

  useEffect(() => {
    setDraft(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    if (draft === searchValue) return undefined;
    timerRef.current = setTimeout(() => {
      onSearchChange(draft);
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [draft, searchValue, onSearchChange]);

  /**
   * Cmd/Ctrl+F focuses the search input, Esc clears it.
   * This matches Gmail / Notion / every editor-ish UI the
   * clinician is already trained on.
   */
  const onContainerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Don't hijack typing inside the search input itself.
      if ((e.key === 'f' || e.key === 'F') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (e.key === 'Escape') {
        if (sheetOpen) {
          e.preventDefault();
          setSheetOpen(false);
          return;
        }
        const target = e.target as HTMLElement | null;
        if (target === searchRef.current && searchValue.length > 0) {
          e.preventDefault();
          setDraft('');
          onSearchChange('');
        }
      }
    },
    [sheetOpen, searchValue, onSearchChange],
  );

  const matchLabel = useMemo(() => {
    if (totalStudents === 0) return null;
    if (filteredCount === totalStudents) {
      return `${totalStudents} ${totalStudents === 1 ? 'student' : 'students'}`;
    }
    return `${filteredCount} of ${totalStudents} students`;
  }, [filteredCount, totalStudents]);

  // ---- Desktop inline row -------------------------------------------
  if (isDesktop) {
    return (
      <div
        className="caseload-toolbar"
        role="search"
        onKeyDown={onContainerKeyDown}
      >
        <div className="caseload-toolbar-search">
          <input
            ref={searchRef}
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            aria-label="Search caseload"
            className="caseload-toolbar-input"
          />
          {matchLabel && (
            <span className="caseload-toolbar-count" aria-live="polite">
              {matchLabel}
            </span>
          )}
        </div>

        <div
          className="caseload-toolbar-chips"
          role="group"
          aria-label="Filter by goal area"
        >
          <FilterChip
            label="All"
            active={goalArea === GOAL_AREA_CHIPS_VALUE_ALL}
            onClick={() => onGoalAreaChange(GOAL_AREA_CHIPS_VALUE_ALL)}
            onClear={undefined}
          />
          {GOAL_AREA_CHIPS.map((c) => (
            <FilterChip
              key={c.value}
              label={c.label}
              active={goalArea === c.value}
              onClear={
                goalArea === c.value
                  ? () => onGoalAreaChange(GOAL_AREA_CHIPS_VALUE_ALL)
                  : undefined
              }
              onClick={() =>
                onGoalAreaChange(
                  goalArea === c.value ? GOAL_AREA_CHIPS_VALUE_ALL : c.value,
                )
              }
            />
          ))}
        </div>

        <div className="caseload-toolbar-secondary">
          <button
            type="button"
            className={
              needsNewSession
                ? 'chip chip-active caseload-toolbar-needs'
                : 'chip caseload-toolbar-needs'
            }
            aria-pressed={needsNewSession}
            onClick={() => onNeedsNewSessionChange(!needsNewSession)}
          >
            Needs new session
          </button>

          <div
            className="caseload-toolbar-chips"
            role="group"
            aria-label="Filter by active goals"
          >
            {ACTIVE_GOALS_CHIPS.map((c) => (
              <FilterChip
                key={c.value}
                label={c.label}
                active={activeGoals === c.value}
                onClear={
                  activeGoals === c.value && c.value !== 'all'
                    ? () => onActiveGoalsChange('all')
                    : undefined
                }
                onClick={() => onActiveGoalsChange(c.value)}
              />
            ))}
          </div>

          <label className="caseload-toolbar-sort">
            <span className="sr-only">Sort by</span>
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value as SortKey)}
            >
              {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABELS[k]}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className={
              selectMode
                ? 'caseload-toolbar-select caseload-toolbar-select-active'
                : 'caseload-toolbar-select'
            }
            onClick={onToggleSelectMode}
            aria-pressed={selectMode}
          >
            {selectMode ? 'Done selecting' : 'Select'}
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              className="caseload-toolbar-clear link-button"
              onClick={() => {
                onGoalAreaChange(GOAL_AREA_CHIPS_VALUE_ALL);
                onNeedsNewSessionChange(false);
                onActiveGoalsChange('all');
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        {bulkActionBar && (
          <div className="caseload-toolbar-bulk-slot">{bulkActionBar}</div>
        )}
      </div>
    );
  }

  // ---- Mobile / tablet toolbar --------------------------------------
  return (
    <div
      className="caseload-toolbar caseload-toolbar-mobile"
      role="search"
      onKeyDown={onContainerKeyDown}
    >
      <div className="caseload-toolbar-search">
        <input
          ref={searchRef}
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          aria-label="Search caseload"
          className="caseload-toolbar-input"
        />
        {matchLabel && (
          <span className="caseload-toolbar-count" aria-live="polite">
            {matchLabel}
          </span>
        )}
      </div>

      <div className="caseload-toolbar-mobile-actions">
        <button
          type="button"
          className="caseload-toolbar-filter-toggle"
          aria-expanded={sheetOpen}
          aria-controls={sheetId}
          onClick={() => setSheetOpen((o) => !o)}
        >
          Filters
          {hasActiveFilters && (
            <span
              className="caseload-toolbar-filter-badge"
              aria-label="Active filters"
            >
              {activeFilterCount({
                goalArea,
                needsNewSession,
                activeGoals,
                sort,
              })}
            </span>
          )}
        </button>
        <button
          type="button"
          className={
            selectMode
              ? 'caseload-toolbar-select caseload-toolbar-select-active'
              : 'caseload-toolbar-select'
          }
          onClick={onToggleSelectMode}
          aria-pressed={selectMode}
        >
          {selectMode ? 'Done' : 'Select'}
        </button>
      </div>

      {sheetOpen && (
        <>
          <div
            className="caseload-toolbar-sheet-scrim"
            onClick={() => setSheetOpen(false)}
            aria-hidden="true"
          />
          <div
            className="caseload-toolbar-sheet"
            id={sheetId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={headingId}
          >
            <div className="caseload-toolbar-sheet-handle" aria-hidden="true" />
            <h2 id={headingId} className="caseload-toolbar-sheet-heading">
              Filter caseload
            </h2>

            <div className="caseload-toolbar-sheet-section">
              <div className="caseload-toolbar-sheet-label">Goal area</div>
              <div
                className="caseload-toolbar-chips"
                role="group"
                aria-label="Filter by goal area"
              >
                <FilterChip
                  label="All"
                  active={goalArea === GOAL_AREA_CHIPS_VALUE_ALL}
                  onClick={() =>
                    onGoalAreaChange(GOAL_AREA_CHIPS_VALUE_ALL)
                  }
                  onClear={undefined}
                />
                {GOAL_AREA_CHIPS.map((c) => (
                  <FilterChip
                    key={c.value}
                    label={c.label}
                    active={goalArea === c.value}
                    onClear={
                      goalArea === c.value
                        ? () => onGoalAreaChange(GOAL_AREA_CHIPS_VALUE_ALL)
                        : undefined
                    }
                    onClick={() =>
                      onGoalAreaChange(
                        goalArea === c.value
                          ? GOAL_AREA_CHIPS_VALUE_ALL
                          : c.value,
                      )
                    }
                  />
                ))}
              </div>
            </div>

            <div className="caseload-toolbar-sheet-section">
              <div className="caseload-toolbar-sheet-label">Sessions</div>
              <button
                type="button"
                className={
                  needsNewSession
                    ? 'chip chip-active caseload-toolbar-needs'
                    : 'chip caseload-toolbar-needs'
                }
                aria-pressed={needsNewSession}
                onClick={() => onNeedsNewSessionChange(!needsNewSession)}
              >
                Needs new session
              </button>
            </div>

            <div className="caseload-toolbar-sheet-section">
              <div className="caseload-toolbar-sheet-label">Active goals</div>
              <div
                className="caseload-toolbar-chips"
                role="group"
                aria-label="Filter by active goals"
              >
                {ACTIVE_GOALS_CHIPS.map((c) => (
                  <FilterChip
                    key={c.value}
                    label={c.label}
                    active={activeGoals === c.value}
                    onClear={
                      activeGoals === c.value && c.value !== 'all'
                        ? () => onActiveGoalsChange('all')
                        : undefined
                    }
                    onClick={() => onActiveGoalsChange(c.value)}
                  />
                ))}
              </div>
            </div>

            <div className="caseload-toolbar-sheet-section">
              <label className="caseload-toolbar-sort">
                <span className="caseload-toolbar-sheet-label">
                  Sort by
                </span>
                <select
                  value={sort}
                  onChange={(e) => onSortChange(e.target.value as SortKey)}
                >
                  {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                    <option key={k} value={k}>
                      {SORT_LABELS[k]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="caseload-toolbar-sheet-actions">
              {hasActiveFilters && (
                <button
                  type="button"
                  className="caseload-toolbar-clear link-button"
                  onClick={() => {
                    onGoalAreaChange(GOAL_AREA_CHIPS_VALUE_ALL);
                    onNeedsNewSessionChange(false);
                    onActiveGoalsChange('all');
                  }}
                >
                  Clear filters
                </button>
              )}
              <button
                type="button"
                className="snap-button"
                onClick={() => setSheetOpen(false)}
              >
                Show {filteredCount} {filteredCount === 1 ? 'student' : 'students'}
              </button>
            </div>
          </div>
        </>
      )}

      {bulkActionBar && (
        <div className="caseload-toolbar-bulk-slot">{bulkActionBar}</div>
      )}
    </div>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  onClear: (() => void) | undefined;
}

function FilterChip({ label, active, onClick, onClear }: FilterChipProps): ReactElement {
  return (
    <button
      type="button"
      className={active ? 'chip chip-active' : 'chip'}
      aria-pressed={active}
      onClick={onClick}
    >
      <span>{label}</span>
      {active && onClear && (
        <span
          className="chip-clear"
          aria-hidden="true"
          // The clear glyph is a sibling inside the same button so
          // keyboard activation hits the parent click handler either
          // way. We stop propagation here so a tap on the × doesn't
          // also re-fire the chip toggle.
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
        >
          ×
        </span>
      )}
    </button>
  );
}

interface ActiveFilterCountInput {
  goalArea: string;
  needsNewSession: boolean;
  activeGoals: ActiveGoalsFilter;
  // sort is intentionally not part of the filter count — it's a
  // view choice that survives "Clear filters" (#487).
  sort?: SortKey;
}

function activeFilterCount(input: ActiveFilterCountInput): number {
  let n = 0;
  if (input.goalArea !== GOAL_AREA_CHIPS_VALUE_ALL) n += 1;
  if (input.needsNewSession) n += 1;
  if (input.activeGoals !== 'all') n += 1;
  return n;
}

// Helpers exposed for tests.
export const __test__ = {
  activeFilterCount,
  ACTIVE_GOAL_LABEL_FOR,
};
